import { useEffect, useRef } from "react"

const VERTEX_SHADER = `
  attribute vec2 position;
  void main() { gl_Position = vec4(position, 0.0, 1.0); }
`

// One time-varying random field for the website (SiteNoise, fixed over the
// page) and each Night Shift screen (the content wells are the glass, the
// chrome the bezel).
const FRAGMENT_SHADER = `
  precision highp float;
  uniform vec2 resolution;
  uniform float time;
  void main() {
    vec2 uv = gl_FragCoord.xy / resolution * (1.0 + time);
    float seed = mod(dot(uv, vec2(12.9898, 78.233)), 3.141592653589793);
    float noise = fract(sin(seed) * 43758.5453);
    gl_FragColor = vec4(vec3(noise), 1.0);
  }
`

// One grain layer sized by its canvas: the class positions it and sets the
// glow and blend (.terminal-screen-grain fills a clipping screen). Still under
// reduced motion; idle while the document is hidden or the canvas is 0×0.
export const ScreenGrain = ({
  className = "terminal-screen-grain",
}: {
  className?: string
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const gl = canvas.getContext("webgl", {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      powerPreference: "low-power",
    })
    if (!gl) return

    const vertex = gl.createShader(gl.VERTEX_SHADER)
    const fragment = gl.createShader(gl.FRAGMENT_SHADER)
    const program = gl.createProgram()
    const buffer = gl.createBuffer()
    const disposeResources = () => {
      gl.deleteBuffer(buffer)
      gl.deleteProgram(program)
      gl.deleteShader(vertex)
      gl.deleteShader(fragment)
    }
    if (!vertex || !fragment || !program || !buffer) {
      disposeResources()
      return
    }
    gl.shaderSource(vertex, VERTEX_SHADER)
    gl.shaderSource(fragment, FRAGMENT_SHADER)
    gl.compileShader(vertex)
    gl.compileShader(fragment)
    gl.attachShader(program, vertex)
    gl.attachShader(program, fragment)
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      disposeResources()
      return
    }
    gl.useProgram(program)
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 3, -1, -1, 3]),
      gl.STATIC_DRAW
    )
    const position = gl.getAttribLocation(program, "position")
    gl.enableVertexAttribArray(position)
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
    const resolution = gl.getUniformLocation(program, "resolution")
    const time = gl.getUniformLocation(program, "time")
    const motion = matchMedia("(prefers-reduced-motion: reduce)")
    let frame = 0
    let lastDraw = -Infinity
    let isCollapsed = false
    const started = performance.now()

    const draw = (now: number) => {
      // Grain needs temporal variation, not display-refresh-rate rendering.
      if (motion.matches || now - lastDraw >= 1000 / 30) {
        gl.uniform1f(time, motion.matches ? 0 : (now - started) / 1000)
        gl.drawArrays(gl.TRIANGLES, 0, 3)
        lastDraw = now
      }
      if (
        !motion.matches &&
        !document.hidden &&
        !isCollapsed &&
        !gl.isContextLost()
      ) {
        frame = requestAnimationFrame(draw)
      }
    }
    const resume = () => {
      cancelAnimationFrame(frame)
      lastDraw = -Infinity
      if (!document.hidden && !isCollapsed) draw(performance.now())
    }
    const resize = () => {
      const { width, height } = canvas.getBoundingClientRect()
      // A hidden well observes as 0×0: hold the last frame until it grows.
      isCollapsed = width === 0 || height === 0
      if (isCollapsed) {
        cancelAnimationFrame(frame)
        return
      }
      // Fine grain on retina without an unbounded backing buffer.
      const ratio = Math.min(
        window.devicePixelRatio || 1,
        1.5,
        Math.sqrt(2_000_000 / Math.max(1, width * height))
      )
      canvas.width = Math.max(1, Math.round(width * ratio))
      canvas.height = Math.max(1, Math.round(height * ratio))
      gl.viewport(0, 0, canvas.width, canvas.height)
      gl.uniform2f(resolution, canvas.width, canvas.height)
      resume()
    }
    const stop = () => cancelAnimationFrame(frame)
    // Also tracks the viewport for a fixed, full-page canvas.
    const observer = new ResizeObserver(resize)
    observer.observe(canvas)
    resize()
    document.addEventListener("visibilitychange", resume)
    motion.addEventListener("change", resume)
    canvas.addEventListener("webglcontextlost", stop)
    return () => {
      stop()
      observer.disconnect()
      document.removeEventListener("visibilitychange", resume)
      motion.removeEventListener("change", resume)
      canvas.removeEventListener("webglcontextlost", stop)
      disposeResources()
    }
  }, [])

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />
}
